import { useQuery } from "@tanstack/react-query";
import { RunStatusEnum } from "enums/RunStatusEnum";
import { RunModel } from "models/RunModel";
import { useApi } from "providers/Api";
import { useCustomParams } from "providers/Params";
import { usePermissions } from "providers/Permissions";
import { useToasts } from "providers/Toasts";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useCallback, useMemo } from "react";

import { RunContext } from "./RunContext";

export const RunProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { runApi } = useApi();
  const { runId } = useCustomParams();
  const { currentUserEmail, handleHttpError } = usePermissions();
  const i18n = useTranslations();
  const toasts = useToasts();

  // prettier-ignore
  const { isLoading, refetch, data, error } = useQuery({
    queryKey: ["run", runId],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!runId) throw new Error("No runId provided");
      return runApi.getRun(runId, signal);
    },
    enabled: !!runId,
  });

  if (error) handleHttpError(error);

  const reloadRun = useCallback(async () => {
    const result = await refetch();
    const newRun = result?.data as RunModel;
    if (newRun) {
      toasts.refreshSuccess(i18n.Run, newRun?.title);
    }
  }, [refetch, i18n, toasts]);

  const contextValue = useMemo(() => {
    const run = isLoading ? undefined : data;
    return {
      runId,
      run,
      isLoading,
      isCurrentUserParticipant:
        run?.participants?.some((x) => `${x.userId}`.toLowerCase() === currentUserEmail) ?? false,
      isPublished: run?.status === RunStatusEnum.Published || run?.status === RunStatusEnum.Republished,
      isPreparing: run?.status === RunStatusEnum.Preparing,
      isClosed:
        run?.status === RunStatusEnum.ClosedFromPublished || run?.status === RunStatusEnum.ClosedFromRepublished,
      reloadRun,
    };
  }, [runId, data, isLoading, currentUserEmail, reloadRun]);

  return <RunContext.Provider value={contextValue}>{children}</RunContext.Provider>;
};
